(() => {
  if (customElements.get('mustflor-delivery-picker')) return;

  const MODES = {
    '49819528397092': 'general',
    '49819528462628': 'express',
    '49849842794788': 'sameDay',
    '55809532068132': 'specified',
  };

  // Official non-working weekdays from the Taiwan DGPA government office calendars.
  // These dates affect production and shipping calculations only. Black Cat delivery
  // dates use the separate isDeliveryDay() rule below.
  const TAIWAN_PUBLIC_HOLIDAYS = new Set([
    // 2026 (ROC year 115)
    '2026-01-01', '2026-02-16', '2026-02-17', '2026-02-18', '2026-02-19',
    '2026-02-20', '2026-02-27', '2026-04-03', '2026-04-06', '2026-05-01',
    '2026-06-19', '2026-09-25', '2026-09-28', '2026-10-09', '2026-10-26',
    '2026-12-25',
    // 2027 (ROC year 116)
    '2027-01-01', '2027-02-04', '2027-02-05', '2027-02-08', '2027-02-09',
    '2027-02-10', '2027-03-01', '2027-04-05', '2027-04-06', '2027-04-30',
    '2027-06-09', '2027-09-15', '2027-09-28', '2027-10-11', '2027-10-25',
    '2027-12-24', '2027-12-31',
  ]);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (value) => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    date.setHours(0, 0, 0, 0);
    return formatLocalDate(date) === value ? date : null;
  };

  const isTaiwanPublicHoliday = (date) => TAIWAN_PUBLIC_HOLIDAYS.has(formatLocalDate(date));

  // Production and shipping workday: Monday-Friday, excluding DGPA holidays.
  const isWorkingDay = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !isTaiwanPublicHoliday(date);
  };

  // Black Cat delivery day: Monday-Saturday. Sunday is never selectable.
  const isDeliveryDay = (date) => date.getDay() !== 0;

  const nextWorkingDay = (startDate) => {
    const date = new Date(startDate);
    date.setHours(0, 0, 0, 0);
    do {
      date.setDate(date.getDate() + 1);
    } while (!isWorkingDay(date));
    return date;
  };

  const addWorkingDays = (startDate, count) => {
    const date = new Date(startDate);
    date.setHours(0, 0, 0, 0);
    let added = 0;
    while (added < count) {
      date.setDate(date.getDate() + 1);
      if (isWorkingDay(date)) added += 1;
    }
    return date;
  };

  const addMonthsClamped = (date, months) => {
    const result = new Date(date);
    const originalDay = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(originalDay, lastDay));
    return result;
  };

  const sameDayExpected = (today) => {
    const date = new Date(today);
    date.setHours(0, 0, 0, 0);
    const weekday = date.getDay();

    // Friday-Sunday orders ship on the next working day and arrive one working day later.
    if (weekday === 5 || weekday === 6 || weekday === 0 || !isWorkingDay(date)) {
      return nextWorkingDay(nextWorkingDay(date));
    }
    return nextWorkingDay(date);
  };

  const getModeConfiguration = (mode, now = new Date()) => {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    let minDate;
    let maxDate;
    let label = '指定收件日期';
    let help = '';
    let readOnly = false;

    if (mode === 'general') {
      minDate = addWorkingDays(today, 4);
      maxDate = addWorkingDays(today, 7);
      help = '一般急件可選購買後第 4～7 個工作日範圍內的送達日；配送日可選週一至週六，週日不可指定。';
    } else if (mode === 'express') {
      minDate = addWorkingDays(today, 2);
      maxDate = addWorkingDays(today, 3);
      help = '特急件可選購買後第 2～3 個工作日範圍內的送達日；配送日可選週一至週六，週日不可指定。';
    } else if (mode === 'sameDay') {
      minDate = sameDayExpected(today);
      maxDate = new Date(minDate);
      label = '預計送達日';
      help = today.getDay() >= 5 || today.getDay() === 0
        ? '週五至週日購買：於下一個工作日寄出，預計再下一個工作日送達。'
        : '預計於下一個工作日送達；台灣國定假日依工作日規則順延。';
      readOnly = true;
    } else if (mode === 'specified') {
      minDate = addWorkingDays(today, 8);
      maxDate = addMonthsClamped(minDate, 3);
      label = '指定收件日期 (非急件)';
      help = '非急件最早可選購買後第 8 個工作日起，最遠可選至其後 3 個月；配送日可選週一至週六，週日不可指定。';
    }

    return {
      minDate,
      maxDate,
      defaultDate: minDate ? formatLocalDate(minDate) : '',
      label,
      help,
      readOnly,
    };
  };

  const isSelectableDeliveryDate = (date, minDate, maxDate, mode) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    if (!isDeliveryDay(date)) return false;
    if (minDate && date < minDate) return false;
    if (maxDate && date > maxDate) return false;
    if (mode === 'sameDay' && minDate && formatLocalDate(date) !== formatLocalDate(minDate)) return false;
    return true;
  };

  class MustflorDeliveryPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.closest('form[action*="/cart/add"]') || this.closest('form');
      this.variantInput = this.form?.querySelector('input[name="id"].product-variant-id, input[name="id"]');
      this.dateInput = this.querySelector('input[name="properties[指定收件日]"]');
      this.dateTrigger = this.querySelector('[data-delivery-date-trigger]');
      this.dateDisplay = this.querySelector('[data-delivery-date-display]');
      this.timeSelect = this.querySelector('select[name="properties[收件時段]"]');
      this.errorElement = this.querySelector('[data-delivery-date-error]');
      this.dateField = this.dateInput?.closest('.mustflor-delivery-picker__field');
      this.dateLabel = this.dateField?.querySelector('label');
      this.helpElement = this.dateField?.querySelector('.mustflor-delivery-picker__help');
      this.calendar = this.querySelector('[data-delivery-calendar]');
      this.calendarHeading = this.querySelector('[data-delivery-calendar-heading]');
      this.calendarDays = this.querySelector('[data-delivery-calendar-days]');
      this.previousMonthButton = this.querySelector('[data-delivery-calendar-previous]');
      this.nextMonthButton = this.querySelector('[data-delivery-calendar-next]');
      this.lastVariantId = null;
      this.currentMode = null;

      if (!this.form || !this.variantInput || !this.dateInput || !this.dateTrigger ||
        !this.timeSelect || !this.calendar || !this.calendarDays) return;

      this.setupTimeOptions();
      this.setupCalendarEvents();
      this.syncVisibility(true);

      this.form.addEventListener('submit', (event) => {
        this.syncVisibility(false);
        if (this.hidden) return;
        if (!this.validateDate(true)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);

      this.variantObserver = new MutationObserver(() => this.syncVisibility(false));
      this.variantObserver.observe(this.variantInput, { attributes: true, attributeFilter: ['value', 'disabled'] });

      this.onDocumentChange = () => {
        window.setTimeout(() => this.syncVisibility(false), 0);
        window.setTimeout(() => this.syncVisibility(false), 120);
        window.setTimeout(() => this.syncVisibility(false), 450);
      };
      document.addEventListener('change', this.onDocumentChange, true);
      this.onVariantEvent = () => window.setTimeout(() => this.syncVisibility(false), 0);
      document.addEventListener('variant:change', this.onVariantEvent);
      document.addEventListener('variantChange', this.onVariantEvent);
    }

    disconnectedCallback() {
      this.variantObserver?.disconnect();
      if (this.onDocumentChange) document.removeEventListener('change', this.onDocumentChange, true);
      if (this.onVariantEvent) {
        document.removeEventListener('variant:change', this.onVariantEvent);
        document.removeEventListener('variantChange', this.onVariantEvent);
      }
      if (this.onOutsideClick) document.removeEventListener('click', this.onOutsideClick);
    }

    setupTimeOptions() {
      const options = Array.from(this.timeSelect.options);
      if (options[0]) {
        options[0].value = '上下午皆可收';
        options[0].textContent = '上下午皆可收';
      }
    }

    setupCalendarEvents() {
      this.dateTrigger.addEventListener('click', () => {
        if (this.dateTrigger.disabled) return;
        this.calendar.hidden ? this.openCalendar() : this.closeCalendar();
      });
      this.previousMonthButton?.addEventListener('click', () => this.changeMonth(-1));
      this.nextMonthButton?.addEventListener('click', () => this.changeMonth(1));
      this.calendarDays.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-date]');
        if (!button || button.disabled) return;
        this.selectDate(button.dataset.date);
      });
      this.calendarDays.addEventListener('keydown', (event) => this.onCalendarKeydown(event));
      this.calendar.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCalendar(true);
        }
      });
      this.onOutsideClick = (event) => {
        if (!this.calendar.hidden && !this.contains(event.target)) this.closeCalendar();
      };
      document.addEventListener('click', this.onOutsideClick);
    }

    currentVariantId() {
      return String(this.variantInput?.value || this.variantInput?.getAttribute('value') || '');
    }

    setLabel(text) {
      if (this.dateLabel) this.dateLabel.innerHTML = `${text} <span aria-hidden="true">*</span>`;
    }

    configureMode(mode) {
      const configuration = getModeConfiguration(mode);
      this.currentMode = mode;
      this.minDate = configuration.minDate;
      this.maxDate = configuration.maxDate;
      this.dateInput.min = this.minDate ? formatLocalDate(this.minDate) : '';
      this.dateInput.max = this.maxDate ? formatLocalDate(this.maxDate) : '';
      this.dateInput.value = configuration.defaultDate;
      this.calendarViewDate = parseLocalDate(configuration.defaultDate) || new Date();
      this.timeSelect.value = '上下午皆可收';
      this.dateTrigger.disabled = configuration.readOnly;
      this.dateTrigger.setAttribute('aria-readonly', String(configuration.readOnly));
      this.setLabel(configuration.label);
      this.updateDateDisplay();
      if (this.helpElement) this.helpElement.textContent = configuration.help;
      this.setError('');
      this.closeCalendar();
      this.renderCalendar();
    }

    syncVisibility(force) {
      const variantId = this.currentVariantId();
      const mode = MODES[variantId] || null;
      const active = Boolean(mode);
      this.hidden = !active;
      this.dateInput.disabled = !active;
      this.timeSelect.disabled = !active;

      if (!active) {
        this.dateTrigger.disabled = true;
        this.setError('');
        this.closeCalendar();
        this.lastVariantId = variantId;
        return;
      }
      if (force || this.lastVariantId !== variantId) {
        this.configureMode(mode);
        this.lastVariantId = variantId;
      }
    }

    openCalendar() {
      this.calendar.hidden = false;
      this.dateTrigger.setAttribute('aria-expanded', 'true');
      this.calendarViewDate = parseLocalDate(this.dateInput.value) || this.minDate || new Date();
      this.renderCalendar();
      const selected = this.calendarDays.querySelector('[aria-pressed="true"]');
      const firstEnabled = this.calendarDays.querySelector('button:not(:disabled)');
      (selected || firstEnabled)?.focus();
    }

    closeCalendar(returnFocus = false) {
      if (!this.calendar) return;
      this.calendar.hidden = true;
      this.dateTrigger?.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.dateTrigger?.focus();
    }

    changeMonth(offset) {
      const view = new Date(this.calendarViewDate);
      view.setDate(1);
      view.setMonth(view.getMonth() + offset);
      this.calendarViewDate = view;
      this.renderCalendar();
      this.calendarDays.querySelector('button:not(:disabled)')?.focus();
    }

    selectDate(value) {
      const selected = parseLocalDate(value);
      if (!isSelectableDeliveryDate(selected, this.minDate, this.maxDate, this.currentMode)) return;
      this.dateInput.value = value;
      this.updateDateDisplay();
      this.setError('');
      this.closeCalendar(true);
    }

    updateDateDisplay() {
      const value = this.dateInput.value;
      if (this.dateDisplay) this.dateDisplay.textContent = value || '請選擇日期';
      this.dateTrigger.setAttribute('aria-label', value ? `指定收件日期：${value}` : '選擇指定收件日期');
    }

    renderCalendar() {
      if (!this.calendarViewDate || !this.calendarDays) return;
      const year = this.calendarViewDate.getFullYear();
      const month = this.calendarViewDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedValue = this.dateInput.value;

      if (this.calendarHeading) this.calendarHeading.textContent = `${year} 年 ${month + 1} 月`;
      this.calendarDays.replaceChildren();

      for (let index = 0; index < firstDay.getDay(); index += 1) {
        const placeholder = document.createElement('span');
        placeholder.className = 'mustflor-calendar__empty';
        placeholder.setAttribute('aria-hidden', 'true');
        this.calendarDays.appendChild(placeholder);
      }

      for (let day = 1; day <= lastDay.getDate(); day += 1) {
        const date = new Date(year, month, day);
        const value = formatLocalDate(date);
        const selectable = isSelectableDeliveryDate(date, this.minDate, this.maxDate, this.currentMode);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mustflor-calendar__day';
        button.textContent = String(day);
        button.dataset.date = value;
        button.disabled = !selectable;
        button.setAttribute('aria-disabled', String(!selectable));
        button.setAttribute('aria-label', `${year} 年 ${month + 1} 月 ${day} 日${date.getDay() === 0 ? '，週日不可指定' : ''}`);
        if (formatLocalDate(today) === value) {
          button.classList.add('is-today');
          button.setAttribute('aria-current', 'date');
        }
        if (selectedValue === value) {
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
        } else {
          button.setAttribute('aria-pressed', 'false');
        }
        this.calendarDays.appendChild(button);
      }

      const previousMonthEnd = new Date(year, month, 0);
      const nextMonthStart = new Date(year, month + 1, 1);
      if (this.previousMonthButton) this.previousMonthButton.disabled = Boolean(this.minDate && previousMonthEnd < this.minDate);
      if (this.nextMonthButton) this.nextMonthButton.disabled = Boolean(this.maxDate && nextMonthStart > this.maxDate);
    }

    onCalendarKeydown(event) {
      const button = event.target.closest('button[data-date]');
      if (!button) return;
      const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (event.key === 'PageUp' || event.key === 'PageDown') {
        event.preventDefault();
        this.changeMonth(event.key === 'PageUp' ? -1 : 1);
        return;
      }
      if (!(event.key in offsets) && event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();

      const current = parseLocalDate(button.dataset.date);
      let offset = offsets[event.key] || 0;
      if (event.key === 'Home') offset = -current.getDay();
      if (event.key === 'End') offset = 6 - current.getDay();
      let target = new Date(current);
      target.setDate(target.getDate() + offset);
      const direction = offset < 0 ? -1 : 1;
      while (!isSelectableDeliveryDate(target, this.minDate, this.maxDate, this.currentMode) &&
        (!this.minDate || target >= this.minDate) && (!this.maxDate || target <= this.maxDate)) {
        target.setDate(target.getDate() + direction);
      }
      if (!isSelectableDeliveryDate(target, this.minDate, this.maxDate, this.currentMode)) return;
      this.calendarViewDate = new Date(target);
      this.renderCalendar();
      this.calendarDays.querySelector(`[data-date="${formatLocalDate(target)}"]`)?.focus();
    }

    validateDate(report) {
      if (this.hidden) return true;
      const value = this.dateInput.value;
      const selected = parseLocalDate(value);
      let message = '';

      if (!value) {
        if (report) message = '請先選擇指定收件日期。';
      } else if (!selected) {
        message = '日期格式不正確，請重新選擇。';
      } else if (this.currentMode === 'sameDay' && formatLocalDate(selected) !== formatLocalDate(this.minDate)) {
        message = '當日特急件的預計送達日由系統自動計算。';
      } else if (!isDeliveryDay(selected)) {
        message = '週日無法指定收件，請選擇週一至週六。';
      } else if (!isSelectableDeliveryDate(selected, this.minDate, this.maxDate, this.currentMode)) {
        message = '請選擇此服務可指定的送達日期範圍。';
      }

      this.setError(message);
      if (message && report) this.dateTrigger.focus({ preventScroll: false });
      return !message;
    }

    setError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
      this.dateTrigger?.setAttribute('aria-invalid', String(Boolean(message)));
    }
  }

  customElements.define('mustflor-delivery-picker', MustflorDeliveryPicker);
})();
